import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnInit,
    Output,
    signal,
} from "@angular/core";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { TaskViewApi } from "../s25-task-view/s25.task.view.component";
import { S25LoadingApi } from "../s25-loading/loading.api";
import { TaskChange, TaskService } from "../../services/task/task.service";
import { Task } from "../../pojo/Task";
import TaskListTaskItem = Task.TaskListTaskItem;
import { TaskNormalizeUtil } from "../../services/task/task.normalize.util";
import { TaskUtil } from "./task.util";

/**
 * S25TaskButtonsComponent is usually used to complete multiple tasks at once, often displayed above and below a table
 */
@TypeManagerDecorator("s25-ng-task-buttons")
@Component({
    selector: "s25-ng-task-buttons",
    template: `
        @if (init) {
            <div>
                <s25-loading-inline [model]="{}"></s25-loading-inline>
                <div>
                    <button
                        class="aw-button aw-button--primary c-margin-right--single c-margin-bottom--half"
                        (click)="approve()"
                        [disabled]="!modelBean || !modelBean.tasks || modelBean.tasks.length === 0"
                    >
                        Approve Checked
                    </button>
                    <button
                        class="aw-button aw-button--primary"
                        (click)="deny(3)"
                        [disabled]="!modelBean || !modelBean.tasks || modelBean.tasks.length === 0"
                    >
                        Deny Checked
                    </button>
                </div>

                @if (clickApproved() && showWaitlistConfirm()) {
                    <div class="cn-alert cn-alert--info cn-alert--notRealConflict">
                        <div>Assigning this location will deny {{ conflcitCount() }}! competing requests</div>
                        <div class="c-margin-left--double">
                            <button
                                class="aw-button aw-button--primary"
                                (click)="approve(); showWaitlistConfirm.set(false)"
                                (keydown.enter)="approve(); showWaitlistConfirm.set(false)"
                            >
                                Yes
                            </button>
                            <button
                                class="aw-button aw-button--outline c-margin-left--single"
                                (click)="showWaitlistConfirm.set(false); clickApproved.set(false); setLoading(false)"
                                (keydown.enter)="
                                    showWaitlistConfirm.set(false); clickApproved.set(false); setLoading(false)
                                "
                            >
                                No
                            </button>
                        </div>
                    </div>
                }
            </div>
        }
    `,
    standalone: false,
})
export class S25TaskButtonsComponent implements OnInit {
    @Input() modelBean: { eventId: number; tasks: TaskListTaskItem[] };
    @Output() onRefresh = new EventEmitter();

    init: boolean = false;

    showWaitlistConfirm = signal<boolean>(false);
    clickApproved = signal<boolean>(false);
    conflcitCount = signal<number>(null);

    constructor(
        private elementRef: ElementRef,
        private cd: ChangeDetectorRef,
        private zone: NgZone,
    ) {
        this.elementRef.nativeElement.angBridge = this;
    }

    ngOnInit() {
        this.init = true;
    }

    approve() {
        this.onActionClick(Task.States.Assigned);
    }

    deny() {
        this.onActionClick(Task.States.Denied);
    }

    async onActionClick(action: Task.State) {
        this.setLoading(true);
        let tasks: TaskChange[] = this.modelBean.tasks.map(TaskNormalizeUtil.taskListToTaskChange);

        let waitListEventIds: number[];
        let spaceCompetes: Task.SpaceCompete[];

        if (action === Task.States.Assigned) {
            spaceCompetes = this.modelBean.tasks.flatMap((task) => task.competes.flatMap((compete) => compete));

            waitListEventIds = Array.from(new Set(spaceCompetes.map((space) => space.conflictId)));
            this.conflcitCount.set(waitListEventIds.length);

            if (spaceCompetes.length > 0 && !this.clickApproved()) {
                this.clickApproved.set(true);
                this.showWaitlistConfirm.set(true);
                return;
            }
        }

        const [resp, err] = await S25Util.Maybe(TaskService.changeTask(tasks, action));
        this.setLoading(false);

        if (err) {
            S25Util.showError(err);
        } else {
            const matchedTasks: TaskChange[] = await TaskUtil.deniedCompeteTasks(waitListEventIds, spaceCompetes);
            const [DeniedResp, DenidedErr] = await S25Util.Maybe(
                TaskService.changeTask(matchedTasks, Task.States.Denied),
            );

            if (DenidedErr) {
                S25Util.showError(err);
            }
        }

        let el: any = document;
        TaskViewApi.reset(el);
    }

    setLoading(yes: boolean) {
        if (yes) {
            S25LoadingApi.init(this.elementRef.nativeElement);
        } else {
            S25LoadingApi.destroy(this.elementRef.nativeElement);
        }
        this.cd.detectChanges();
    }
}
